import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublishedArticles } from 'hooks/useArticles';
import { Article } from 'types/article';
import './NewsSection.scss';

// ── Helpers ────────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('vi-VN', {
      day: 'numeric', month: 'long', year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

// ── Skeleton ──────────────────────────────────────────────────────────────────

const NewsSkeleton: React.FC = () => (
  <div className="nws__grid nws__grid--skeleton">
    <div className="nws__skeleton nws__skeleton--featured" />
    <div className="nws__skeleton-list">
      {[1, 2, 3].map(i => (
        <div key={i} className="nws__skeleton nws__skeleton--item" />
      ))}
    </div>
  </div>
);

// ── Sub-components ─────────────────────────────────────────────────────────────

const FeaturedArticle: React.FC<{ art: Article; readMoreLabel: string }> = ({ art, readMoreLabel }) => (
  <Link to={`/tin-tuc/${art.slug}`} className="nws__featured">
    <div className="nws__featured-img-wrap">
      {art.thumbnail
        ? <img src={art.thumbnail} alt={art.title} className="nws__featured-img" loading="lazy" />
        : <div className="nws__featured-img-fallback"><span>{art.title.charAt(0)}</span></div>
      }
    </div>
    <div className="nws__featured-body">
      <span className="nws__date"><Calendar size={12} /> {formatDate(art.createdAt)}</span>
      <h3 className="nws__featured-title">{art.title}</h3>
      <p className="nws__featured-excerpt">{art.excerpt}</p>
      <div className="nws__featured-footer">
        <span className="nws__read-more">{readMoreLabel} <ArrowRight size={13} /></span>
        <span className="nws__author"><Users size={12} /> {art.author}</span>
      </div>
    </div>
  </Link>
);

const SideArticle: React.FC<{ art: Article; readMoreLabel: string }> = ({ art, readMoreLabel }) => (
  <Link to={`/tin-tuc/${art.slug}`} className="nws__item">
    <div className="nws__item-img-wrap">
      {art.thumbnail
        ? <img src={art.thumbnail} alt={art.title} className="nws__item-img" loading="lazy" />
        : <div className="nws__item-img-fallback"><span>{art.title.charAt(0)}</span></div>
      }
    </div>
    <div className="nws__item-body">
      <span className="nws__date"><Calendar size={11} /> {formatDate(art.createdAt)}</span>
      <h4 className="nws__item-title">{art.title}</h4>
      <p className="nws__item-excerpt">{art.excerpt}</p>
      <span className="nws__read-more">{readMoreLabel} <ArrowRight size={12} /></span>
    </div>
  </Link>
);

// ── Section ────────────────────────────────────────────────────────────────────

const NewsSection: React.FC = () => {
  const { t } = useTranslation();
  const { data: articles, loading } = usePublishedArticles();

  // Không render null — luôn giữ section trong DOM để tránh layout shift
  // và tránh data-reveal observer miss elements
  const featured = articles?.[0] ?? null;
  const sideArticles = articles?.slice(1, 4) ?? [];
  const readMore = t('trangChu.news.readMore');
  const hasArticles = articles && articles.length > 0;

  // Ẩn section hoàn toàn chỉ khi: đã load xong nhưng không có bài nào
  if (!loading && !hasArticles) return null;

  return (
    <section className="nws" aria-label={t('trangChu.news.sectionAria')}>
      <div className="nws__container">

        {/* Header — luôn hiển thị */}
        <div className="nws__header">
          <span className="nws__eyebrow">
            <Calendar size={12} strokeWidth={2.5} />
            {t('trangChu.news.eyebrow')}
          </span>
          <h2 className="nws__heading">{t('trangChu.news.heading')}</h2>
          <p className="nws__subtext">{t('trangChu.news.subtext')}</p>
        </div>

        {/* Skeleton khi đang tải */}
        {loading && <NewsSkeleton />}

        {/* Nội dung thật khi đã có dữ liệu */}
        {!loading && hasArticles && featured && (
          <div className="nws__grid">
            <FeaturedArticle art={featured} readMoreLabel={readMore} />
            <div className="nws__list">
              {sideArticles.map(art => (
                <SideArticle key={art.id} art={art} readMoreLabel={readMore} />
              ))}
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default NewsSection;
